import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import {
  CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES,
  lotteryService,
  type BetSlip,
  type BetSlipItem,
  type LotteryRound,
} from '../../services/lotteryService'
import * as walletService from '../../services/walletService'
import { getWinningNumbers, hasAnyResult } from '../../utils/resultDisplayUtils'

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

function formatDrawDateTime(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

/** ดึง 3ตัวบน, 2ตัวล่าง จาก round (รองรับทั้ง government และ yeekee) */
function getResultFromRound(round: LotteryRound | null): {
  threeUp: string
  twoDown: string
} {
  if (!round) return { threeUp: '', twoDown: '' }
  const yeekee = (round as { yeekee?: { result?: { threeDigitTop?: string; twoDigitDown?: string } } }).yeekee?.result
  if (yeekee) {
    return {
      threeUp: yeekee.threeDigitTop ?? '',
      twoDown: yeekee.twoDigitDown ?? '',
    }
  }
  const wn = getWinningNumbers(round)
  return { threeUp: wn.threeUp, twoDown: wn.twoDown }
}

function getItemDiscountPercent(item: BetSlipItem): number | null {
  const lockedDiscount = Number(item.lockedDiscount)
  if (Number.isFinite(lockedDiscount)) return lockedDiscount

  const snapshotDiscount = Number((item.snapshotPrizeConfig as { discount?: number } | undefined)?.discount)
  if (Number.isFinite(snapshotDiscount)) return snapshotDiscount

  const amount = Number(item.amount)
  const effectiveAmount = Number(item.effectiveAmount)
  if (Number.isFinite(amount) && amount > 0 && Number.isFinite(effectiveAmount)) {
    return ((amount - effectiveAmount) / amount) * 100
  }

  return null
}

function formatDiscountPercent(value: number | null): string {
  if (value == null) return '-'
  const normalized = Math.max(0, value)
  const rounded = Number.isInteger(normalized) ? normalized.toFixed(0) : normalized.toFixed(2)
  return `${rounded}%`
}

function getProfitBadge(itemProfit: number | null): {
  className: string
  text: string
} {
  if (itemProfit == null) {
    return {
      className: 'bg_op_secondary c_tertiary',
      text: '-',
    }
  }

  if (itemProfit > 0) {
    return {
      className: 'bg_success c_tertiary',
      text: `ได้ ${walletService.formatCurrency(itemProfit)}`,
    }
  }

  if (itemProfit < 0) {
    return {
      className: 'bg_primary c_tertiary',
      text: `เสีย ${walletService.formatCurrency(Math.abs(itemProfit))}`,
    }
  }

  return {
    className: 'bg_secondary c_primary',
    text: `เสมอ ${walletService.formatCurrency(0)}`,
  }
}

function getCustomerCancelWindowMinutes(slip: BetSlip | null): number {
  const configured = Number(slip?.customerCancelWindowMinutes)
  return Number.isFinite(configured) && configured > 0
    ? configured
    : CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES
}

function canCustomerCancelSlip(slip: BetSlip | null, round: LotteryRound | null): boolean {
  if (!slip) return false

  const status = String(slip.status || '').toLowerCase()
  if (slip.isSettled || ['cancelled', 'refunded'].includes(status)) return false

  const createdAt = slip.createdAt ? new Date(slip.createdAt).getTime() : NaN
  if (!Number.isFinite(createdAt)) return false

  if (round && String(round.status || '').toLowerCase() !== 'open') return false

  return Date.now() - createdAt <= getCustomerCancelWindowMinutes(slip) * 60 * 1000
}

function getSlipRefundDisplay(
  slip: BetSlip,
  round: LotteryRound | null
): {
  amount: number
  title: string
  subtitle: string
} {
  const cancelWindowMinutes = getCustomerCancelWindowMinutes(slip)
  const status = String(slip.status || '').toLowerCase()
  const persistedRefundAmount = Number(slip.refundAmount)
  const fallbackRefundAmount = Number(slip.totalEffectiveAmount ?? slip.totalAmount ?? 0)
  const amount = Number.isFinite(persistedRefundAmount) && persistedRefundAmount > 0
    ? persistedRefundAmount
    : fallbackRefundAmount
  const refundedAt = String(slip.refundedAt || slip.updatedAt || slip.createdAt || '')

  if (status === 'cancelled' || status === 'refunded') {
    return {
      amount: Number.isFinite(amount) ? amount : 0,
      title: 'ยอดคืนโพย',
      subtitle: `คืนโพยเมื่อ ${refundedAt ? formatDate(refundedAt) : '-'}`,
    }
  }

  if (canCustomerCancelSlip(slip, round)) {
    return {
      amount: Number.isFinite(amount) ? amount : 0,
      title: 'ค่าคืนโพย',
      subtitle: `คืนได้ภายใน ${cancelWindowMinutes} นาทีตามยอดสุทธิ`,
    }
  }

  return {
    amount: Number.isFinite(amount) ? amount : 0,
    title: 'ยอดสุทธิคืนโพย',
    subtitle: 'ระบบคืนตามยอดสุทธิของโพยเมื่อยังอยู่ในเงื่อนไข',
  }
}

/** หน้ารายละเอียดโพย — โครงสร้างตรงกับ polehuay-detail.html */
const ModernLobbyHuaySlipDetailPage: React.FC = () => {
  const { slipId } = useParams<{ slipId: string }>()
  const [slip, setSlip] = useState<BetSlip | null>(null)
  const [round, setRound] = useState<LotteryRound | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slipId) {
      setLoading(false)
      setError('ไม่พบรหัสโพย')
      return
    }
    lotteryService
      .getSlip(slipId)
      .then(async (data) => {
        setSlip(data ?? null)
        if (!data) {
          setError('ไม่พบข้อมูลโพย')
          return
        }
        const rid = data.roundId
          ? String(typeof data.roundId === 'object' ? (data.roundId as { _id?: string })._id : data.roundId)
          : null
        if (rid) {
          const [r] = await lotteryService.getLotteryRounds({ roundId: rid, limit: 1 })
          setRound(r ?? null)
        }
      })
      .catch(() => setError('โหลดโพยไม่สำเร็จ'))
      .finally(() => setLoading(false))
  }, [slipId])

  const handleCancelSlip = async () => {
    if (!slip) return
    const cancelWindowMinutes = getCustomerCancelWindowMinutes(slip)

    if (!canCustomerCancelSlip(slip, round)) {
      await Swal.fire({
        icon: 'warning',
        title: 'ไม่สามารถคืนโพยได้',
        text: `คืนโพยได้ภายใน ${cancelWindowMinutes} นาทีหลังจากที่แทง และต้องยังไม่ปิดรับแทง`,
        confirmButtonColor: '#0d6efd',
      })
      return
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'ยืนยันการคืนโพย',
      html: `คุณต้องการคืนโพย <strong>${slip.slipId ?? slip._id ?? '-'}</strong> ใช่หรือไม่?<br/><small>ระบบจะคืนยอดสุทธิของโพยกลับเข้ากระเป๋า</small>`,
      showCancelButton: true,
      confirmButtonText: 'คืนโพย',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#d97706',
      cancelButtonColor: '#6b7280',
    })

    if (!result.isConfirmed) return

    const response = await lotteryService.cancelSlip(String(slip.slipId ?? slip._id ?? ''))
    if (!response.success) {
      await Swal.fire({
        icon: 'error',
        title: 'คืนโพยไม่สำเร็จ',
        text: response.message || 'กรุณาลองใหม่อีกครั้ง',
        confirmButtonColor: '#0d6efd',
      })
      return
    }

    const payload = response.data as { refunded?: boolean; refundError?: string | null } | undefined
    if (payload?.refunded === false) {
      await Swal.fire({
        icon: 'warning',
        title: 'ยกเลิกโพยแล้ว แต่คืนเครดิตไม่สำเร็จ',
        text: payload.refundError || 'ระบบจะพยายามคืนเครดิตอัตโนมัติอีกครั้ง หากยอดไม่กลับภายในไม่กี่นาที กรุณาติดต่อแอดมิน',
        confirmButtonColor: '#0d6efd',
      })
    } else {
      await Swal.fire({
        icon: 'success',
        title: 'คืนโพยสำเร็จ',
        text: response.message || 'ระบบคืนโพยเรียบร้อยแล้ว',
        confirmButtonColor: '#0d6efd',
      })
    }

    const data = await lotteryService.getSlip(slipId || '')
    setSlip(data ?? null)
  }

  if (loading) {
    return (
      <div className="c_quinary text-center py-5">
        <p className="c_tertiary">กำลังโหลด...</p>
      </div>
    )
  }

  if (error || !slip) {
    return (
      <div className="c_quinary text-center py-5">
        <p className="c_primary">{error ?? 'ไม่พบข้อมูลโพย'}</p>
        <Link to="/lobby-huay/slip" className="mt-2 d-inline-block c_tertiary">
          <i className="fas fa-chevron-left me-1" /> กลับไปโพยหวย
        </Link>
      </div>
    )
  }

  const yeekeeResult = round
    ? (round as { yeekee?: { result?: { sumTotal?: string | number }; shotCount?: number } }).yeekee?.result
    : null
  const hasRoundResult = Boolean(hasAnyResult(round) || yeekeeResult?.sumTotal != null)

  const winLoss =
    hasRoundResult && slip.isSettled && slip.totalWinAmount != null
      ? (slip.totalWinAmount as number) - ((slip.totalEffectiveAmount as number) ?? 0)
      : null

  const { threeUp, twoDown } = getResultFromRound(round)
  const displaySlipId = slip.slipId ?? slip._id ?? '-'
  const sumTotal = yeekeeResult?.sumTotal != null ? String(yeekeeResult.sumTotal) : null
  const canCancelNow = canCustomerCancelSlip(slip, round)
  const refundDisplay = getSlipRefundDisplay(slip, round)

  return (
    <div className="c_quinary">
      <div className="c_quinary text-center position-relative">
        <Link to="/lobby-huay/slip" className="link_back">
          กลับ
        </Link>
        <h3 className="c_tertiary font_md mt-2 mb-0">
          <i className="fas fa-clipboard-list me-1" /> โพยใบที่: {displaySlipId}
        </h3>
        {canCancelNow && (
          <div className="mt-2">
            <button
              type="button"
              className="border-0 bg-warning px-3 py-1 border_5"
              onClick={() => void handleCancelSlip()}
            >
              คลิกเพื่อคืนโพย
            </button>
          </div>
        )}
      </div>

      {/* การ์ดหลัก — รหัสการแทง, หวย, ทำรายการเมื่อ, เงินเดิมพัน, สรุปผล */}
      <div className="row mx__1 mt-3" style={{ rowGap: '0.5rem' }}>
        <div className="col-12 px-1">
          <div className="border_5 bdc_secondary bg_secondary p-2">
            <div className="bg_gd_secondary c_tertiary text-center border_5 px-1 bx_sd mb-2">
              <small>
                <span className="font_md">
                  <i className="fas fa-clipboard-list me-1" /> รหัสการแทง
                </span>{' '}
                {displaySlipId}
              </small>
            </div>

            <div
              className="d-grid gap-2 align-items-center mt-1"
              style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}
            >
              <h6 className="c_primary font_md text-center">{slip.huayName ?? '-'}</h6>
              <small className="c_secondary text-center">
                {round?.betEndTime
                  ? formatDrawDateTime(round.betEndTime)
                  : slip.roundNumber
                    ? `รอบที่ ${slip.roundNumber}`
                    : '-'}
              </small>
            </div>

            <div className="bg_quaternary c_secondary text-center border_5 px-1 bx_sd mt-1 mb-2">
              <small>
                <span className="font_md"> ทำรายการเมื่อ:</span>
                <i className="fas fa-calendar-alt mx-1" />{' '}
                {slip.createdAt ? formatDate(slip.createdAt) : '-'}
              </small>
            </div>

            <div
              className="d-grid gap-2 mt-1"
              style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}
            >
              <div className="bg_gd_primary border_5 bdc_secondary p-1 text-center">
                <div className="bg_op_secondary px-1 py-1 font_rg border_5">
                  <small className="c_tertiary">เงินเดิมพันทั้งหมด</small>
                </div>
                <div className="px-1 font_rg c_tertiary">
                  <h4>{walletService.formatCurrency(slip.totalAmount ?? 0)}</h4>
                </div>
              </div>
              <div className="bg_gd_primary border_5 bdc_secondary p-1 text-center">
                <div className="bg_op_secondary px-1 py-1 font_rg border_5">
                  <small className="c_tertiary">สรุปผลแพ้/ชนะ</small>
                </div>
                <div className="px-1 font_rg c_tertiary">
                  <h4>{winLoss != null ? walletService.formatCurrency(winLoss) : '-'}</h4>
                </div>
              </div>
            </div>
            <div className="bg_gd_primary border_5 bdc_secondary p-1 text-center mt-2">
              <div className="bg_op_secondary px-1 py-1 font_rg border_5">
                <small className="c_tertiary">{refundDisplay.title}</small>
              </div>
              <div className="px-1 font_rg c_tertiary">
                <h4>{walletService.formatCurrency(refundDisplay.amount)}</h4>
                <small>{refundDisplay.subtitle}</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ผลรางวัลที่ออก */}
      {(threeUp || twoDown || round) && (
        <div className="row mx__1 mt-3" style={{ rowGap: '0.5rem' }}>
          <div className="col-12 px-1">
            <h3 className="c_tertiary font_md text-center">ผลรางวัลที่ออก</h3>
            <div
              className="d-grid gap-2 mt-3"
              style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}
            >
              <div className="bdc_secondary bg_secondary border_5 p-2 text-center">
                <div className="bg_gd_primary px-1 py-1 font_rg border_5">
                  <small className="c_tertiary">3ตัวบน</small>
                </div>
                <div className="px-1 font_rg c_secondary">
                  <h4>{threeUp || 'รอผล'}</h4>
                </div>
              </div>
              <div className="bdc_secondary bg_secondary border_5 p-2 text-center">
                <div className="bg_gd_primary px-1 py-1 font_rg border_5">
                  <small className="c_tertiary">2ตัวล่าง</small>
                </div>
                <div className="px-1 font_rg c_secondary">
                  <h4>{twoDown || 'รอผล'}</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* รายการแทง — การ์ดแต่ละรายการ */}
      <div className="row mx__1 mt-3" style={{ rowGap: '0.5rem' }}>
        {slip.betItems && slip.betItems.length > 0 ? (
          slip.betItems.map((item: BetSlipItem, idx: number) => {
            const isWinner = hasRoundResult ? ((item.isWinner as boolean) ?? item.status === 'won') : null
            const winAmount = (item.winAmount as number) ?? (item.potentialPayout ?? 0)
            const discountPercent = getItemDiscountPercent(item)
            const itemProfit =
              hasRoundResult && slip.isSettled && isWinner && winAmount != null
                ? winAmount - (item.effectiveAmount ?? item.amount ?? 0)
                : hasRoundResult && slip.isSettled && isWinner === false
                  ? -(item.effectiveAmount ?? item.amount ?? 0)
                  : null
            const profitBadge = getProfitBadge(itemProfit)

            return (
              <div key={idx} className="col-12 px-1">
                <div className="border_5 bdc_secondary bg_secondary p-2">
                  <div className="box_row justify-content-between">
                    <h6 className="c_primary font_md">ลำดับที่ {idx + 1}.</h6>
                    <h6
                      className={`${isWinner === null ? 'bg_secondary' : isWinner ? 'bg_success' : 'bg_primary'} c_tertiary px-1 border_5`}
                    >
                      {isWinner === null ? 'รอผล' : isWinner ? 'ถูกรางวัล' : 'ไม่ถูกรางวัล'}
                    </h6>
                  </div>

                  <div
                    className="d-grid gap-2 bg_quaternary align-items-center border_5 p-2 mt-1"
                    style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}
                  >
                    <div>
                      <div className="px-1 font_rg text-center c_tertiary">
                        <h4>{item.number}</h4>
                      </div>
                      <div
                        className="bg_gd_secondary px-1 mx-auto font_rg border_5"
                        style={{ width: 'fit-content' }}
                      >
                        <h6 className="c_tertiary">{item.prizeTypeName ?? item.prizeTypeCode ?? '-'}</h6>
                      </div>
                    </div>

                    <div>
                      <div className="row mx__1" style={{ rowGap: '0.5rem' }}>
                        <div className="col-12 px-1">
                          <div className="text-center c_tertiary bg_op_secondary border_5 bdc_secondary px-1">
                            <small>เลขที่ออก</small>
                            <h5 className="font_md mb-0">
                              {hasRoundResult ? (threeUp || twoDown || 'รอผล') : 'รอผล'}
                            </h5>
                          </div>
                        </div>
                        <div className="col-6 px-1">
                          <div className="text-center c_secondary bg_secondary border_5 bdc_secondary px-1">
                            <small>จ่าย</small>
                            <h5 className="font_md c_success mb-0">
                              {item.lockedPayoutRate ?? (item.potentialPayout != null && item.amount
                                ? Math.round((item.potentialPayout ?? 0) / item.amount)
                                : '-')}
                            </h5>
                          </div>
                        </div>
                        <div className="col-6 px-1">
                          <div className="text-center c_secondary bg_secondary border_5 bdc_secondary px-1">
                            <small>ลด</small>
                            <h5 className="font_md c_primary mb-0">
                              {formatDiscountPercent(discountPercent)}
                            </h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className="d-grid gap-2 align-items-center mt-1"
                    style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}
                  >
                    <small className="c_primary text-center font_md">
                      ราคาแทง{' '}
                      <span className="px-1 border_5 bg_gd_primary c_tertiary">
                        {walletService.formatCurrency(item.amount ?? 0)}
                      </span>
                    </small>
                    <small className="c_primary text-center font_md">
                      ผลได้/เสีย{' '}
                      <span className={`px-2 border_5 ${profitBadge.className}`}>
                        {profitBadge.text}
                      </span>
                    </small>
                  </div>
                </div>
              </div>
            )
          })
        ) : (
          <div className="col-12 px-1">
            <div className="border_5 bdc_secondary bg_secondary p-3 text-center">
              <p className="c_secondary mb-0">ไม่มีรายการแทง</p>
            </div>
          </div>
        )}
      </div>

      {/* ผลรวมเลข — สำหรับหวยยี่กี */}
      {(sumTotal || round) && (
        <div className="d-grid gap-3 mt-3">
          <h3 className="text-center c_tertiary font_md">ผลรวมเลข</h3>
          {sumTotal ? (
            <h1 className="text-center c_tertiary bdc_primary bg_op_secondary border_5 px-2 py-1 font_md">
              {sumTotal}
            </h1>
          ) : (
            <p className="text-center c_secondary mb-0">รอผล</p>
          )}
          <div
            className="d-grid gap-2"
            style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}
          >
            <div className="bdc_secondary bg_secondary border_5 p-2 text-center">
              <div className="bg_gd_tertiary px-1 py-1 font_rg border_5">
                <small className="c_secondary">รอบวันที่</small>
              </div>
              <div className="px-1 font_rg c_secondary">
                <h4 className="mb-0">
                  {round?.betEndTime ? formatDrawDateTime(round.betEndTime) : slip.roundNumber ?? '-'}
                </h4>
              </div>
            </div>
            <div className="bdc_secondary bg_secondary border_5 p-2 text-center">
              <div className="bg_gd_tertiary px-1 py-1 font_rg border_5">
                <small className="c_secondary">รอบที่</small>
              </div>
              <div className="px-1 font_rg c_secondary">
                <h4 className="mb-0">{slip.roundNumber ?? '-'}</h4>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ModernLobbyHuaySlipDetailPage
